import React, { useState, useEffect } from 'react';
import {
  X,
  Building2,
  User,
  Phone,
  Mail,
  FileText,
  MapPin,
  Save,
  Loader2
} from 'lucide-react';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import styles from './VendorDrawer.module.css';

const VendorDrawer = ({ isOpen, onClose, initialData = null, onSave }) => {
  const [formData, setFormData] = useState({
    vendor_name: '',
    contact_person_name: '',
    phone: '',
    email: '',
    gst_number: '',
    address: ''
  });
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const mode = initialData ? 'edit' : 'add';

  useEffect(() => {
    if (isOpen) {
      if (initialData && initialData.id) {
        fetchVendorDetails(initialData.id);
      } else {
        setFormData({
          vendor_name: '',
          contact_person_name: '',
          phone: '',
          email: '',
          gst_number: '',
          address: ''
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, isOpen]);

  const fetchVendorDetails = async (id) => {
    setIsFetching(true);
    try {
      const response = await api.get(`/vendors/${id}`);
      if (response.data.success) {
        const vendor = response.data.data;
        setFormData({
          vendor_name: vendor.vendor_name || '',
          contact_person_name: vendor.contact_person_name || '',
          phone: vendor.phone || '',
          email: vendor.email || '',
          gst_number: vendor.gst_number || '',
          address: vendor.address || ''
        });
      }
    } catch (error) {
      console.error('Error fetching vendor details:', error);
      toast.error('Failed to load vendor details');
    } finally {
      setIsFetching(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let response;
      if (mode === 'edit') {
        response = await api.put(`/vendors/${initialData.id}`, formData);
      } else {
        response = await api.post('/vendors', formData);
      }

      if (response.data.success) {
        toast.success(`Vendor ${mode === 'edit' ? 'updated' : 'created'} successfully`);
        onSave();
        onClose();
      }
    } catch (error) {
      console.error('Error saving vendor:', error);
      toast.error(error.response?.data?.message || 'Failed to save vendor');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles['drawer-backdrop']} onClick={onClose}>
      <div className={styles['drawer-container']} onClick={e => e.stopPropagation()}>
        <div className={styles['drawer-header']}>
          <div className={styles['header-title-wrapper']}>
            <h2>{mode === 'add' ? 'Add Vendor' : 'Edit Vendor'}</h2>
            <p>{mode === 'add' ? 'Register a new supplier to the system' : 'Update vendor details'}</p>
          </div>
          <button className={styles['close-btn']} onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSave} className={styles['drawer-form']}>
          {isFetching ? (
            <div className={styles['fetching-state']}>
              <Loader2 size={40} className={styles['spinner']} />
              <p>Fetching technical details...</p>
            </div>
          ) : (
            <>
              <div className={styles['form-scroll-area']}>
                <div className={styles['form-group']}>
                  <label><Building2 size={16} /> Vendor Name <span className={styles['required-star']}>*</span></label>
                  <input
                    type="text"
                    name="vendor_name"
                    placeholder="e.g. Reliance Electric Supplies"
                    value={formData.vendor_name}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                  />
                </div>

                <div className={styles['form-group']}>
                  <label><User size={16} /> Contact Person</label>
                  <input
                    type="text"
                    name="contact_person_name"
                    placeholder="e.g. Rajesh Kumar"
                    value={formData.contact_person_name}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </div>

                <div className={styles['form-group']}>
                  <label><Phone size={16} /> Phone Number</label>
                  <input
                    type="text"
                    name="phone"
                    placeholder="+91 98765 43210"
                    value={formData.phone}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </div>

                <div className={styles['form-group']}>
                  <label><Mail size={16} /> Email Address</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="vendor@example.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </div>

                <div className={styles['form-group']}>
                  <label><FileText size={16} /> GST Number</label>
                  <input
                    type="text"
                    name="gst_number"
                    placeholder="22AAAAA0000A1Z5"
                    value={formData.gst_number}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </div>

                <div className={styles['form-group']}>
                  <label><MapPin size={16} /> Address</label>
                  <textarea
                    name="address"
                    placeholder="Full street address, City, State, PIN"
                    value={formData.address}
                    onChange={handleInputChange}
                    disabled={loading}
                  ></textarea>
                </div>
              </div>

              <div className={styles['drawer-footer']}>
                <button type="button" className={styles['cancel-btn']} onClick={onClose} disabled={loading}>
                  Cancel
                </button>
                <button type="submit" className={styles['save-btn']} disabled={loading}>
                  {loading ? <Loader2 size={18} className={styles['spinner']} /> : <Save size={18} />}
                  <span>{mode === 'add' ? 'Save Vendor' : 'Update Vendor'}</span>
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default VendorDrawer;
